import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, withCtx as _withCtx, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, mergeProps as _mergeProps } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "text-sm text-gray-600"
}
const _hoisted_2 = {
  key: 2,
  class: "text-sm text-gray-600"
}
const _hoisted_3 = {
  key: 3,
  class: "flex flex-wrap items-end gap-3"
}
const _hoisted_4 = {
  key: 4,
  class: "text-sm text-gray-600"
}
const _hoisted_5 = { class: "-my-3.5" }
const _hoisted_6 = {
  key: 5,
  "data-extras": "",
  class: "space-y-3"
}
const _hoisted_7 = { class: "flex flex-wrap gap-2" }
const _hoisted_8 = ["aria-pressed", "onClick"]
const _hoisted_9 = {
  key: 0,
  class: "text-sm text-gray-600"
}
const _hoisted_10 = { class: "-my-3.5 divide-y divide-[#eef0f3]" }
const _hoisted_11 = {
  "data-more-options": "",
  class: "border-t border-gray-200 py-[22px]"
}
const _hoisted_12 = { class: "cursor-pointer text-sm font-semibold text-gray-900" }
const _hoisted_13 = { class: "font-normal text-gray-500" }
const _hoisted_14 = { class: "mt-5 rounded-[10px] border border-gray-200 bg-white px-[22px] py-5 min-[900px]:ml-[252px]" }

import { CheckIcon } from '@heroicons/vue/20/solid'
import { computed, onMounted, ref } from 'vue'

import SegmentedControl from '@/components/neue/SegmentedControl.vue'
import SelectInput from '@/components/neue/SelectInput.vue'
import AmountField from '@/components/neue/wizard/AmountField.vue'
import NumberStepper from '@/components/neue/wizard/NumberStepper.vue'
import PillRadioGroup from '@/components/neue/wizard/PillRadioGroup.vue'
import SelectionCard from '@/components/neue/wizard/SelectionCard.vue'
import SelectionGroup from '@/components/neue/wizard/SelectionGroup.vue'
import ToggleRow from '@/components/neue/wizard/ToggleRow.vue'
import WeekdayPicker from '@/components/neue/wizard/WeekdayPicker.vue'
import WizardButton from '@/components/neue/wizard/WizardButton.vue'
import WizardSection from '@/components/neue/wizard/WizardSection.vue'
import { injectWizard } from '@/composables/useWizardContext'
import {
  CUSTOM_INTERVAL,
  customValid,
  defaultInterval,
  extraIntervalOptions,
  INTERVAL_PRESETS,
  INTERVAL_UNITS,
  intervalAmount,
  intervalKey,
  intervalLabel,
  matchingPreset,
  paymentOptions,
  sectionCopy,
  switchCopy,
  WEEKDAY_SCHEDULE,
  WEEKDAYS,
  WEEKDAYS_INTERVAL
} from '@/config/membershipTierWizardSteps'

const CUSTOM_HELP = 'Any number of days, weeks, months or years'
const WEEKDAYS_HELP = 'Orders repeat every week on the days you pick.'


export default {
  __name: 'StepOrdering',
  props: { stores: { type: Array, default: () => [] } },
  setup(__props) {

const PAYMENT_ICONS = { always: 'card', never: 'bell', choice: 'check' }

const props = __props

const wizard = injectWizard()
const ordering = computed(() => wizard.data.ordering)
const payments = computed(() => paymentOptions(wizard.data))
const extraOptions = computed(() => extraIntervalOptions(wizard.data))
const switching = computed(() => switchCopy(wizard.data))
const frequencyOptions = [
  ...INTERVAL_PRESETS.map(preset => ({ name: intervalLabel(preset), value: intervalKey(preset) })),
  { name: 'On set weekdays', value: WEEKDAYS_INTERVAL },
  { name: 'Custom…', value: CUSTOM_INTERVAL }
]
const customPicked = computed(() => ordering.value.every === CUSTOM_INTERVAL)
const weekdaysPicked = computed(() => ordering.value.every === WEEKDAYS_INTERVAL)
const repeatNote = ref('')
const extraNote = ref('')

const setEvery = key => {
  ordering.value.every = key
  ordering.value.alsoOffer = ordering.value.alsoOffer.filter(extra => extra !== key)
}

const settleRepeat = () => {
  const custom = ordering.value.custom
  const preset = customPicked.value && customValid(custom) && matchingPreset(custom)
  if (!preset) return
  const label = intervalLabel(preset)
  setEvery(intervalKey(preset))
  repeatNote.value = `${intervalAmount(custom)} is the same as ${label}, so ${label} is selected.`
}

const setUnit = unit => {
  ordering.value.custom.unit = unit
  settleRepeat()
}

const toggleExtra = key => {
  const extras = ordering.value.alsoOffer
  ordering.value.alsoOffer = extras.includes(key) ? extras.filter(extra => extra !== key) : [...extras, key]
}

const removeExtra = index => {
  ordering.value.customExtras = ordering.value.customExtras.filter((_, i) => i !== index)
}

const settleExtra = index => {
  const extra = ordering.value.customExtras[index]
  if (!extra || extra.unit === WEEKDAY_SCHEDULE || !customValid(extra)) return
  const repeat = defaultInterval(wizard.data)
  const preset = matchingPreset(extra)
  const sameAsRepeat = repeat && intervalKey(repeat) === intervalKey(preset ?? extra)
  if (!preset && !sameAsRepeat) return
  const amount = intervalAmount(extra)
  removeExtra(index)
  const label = intervalLabel(preset ?? extra)
  if (sameAsRepeat) {
    extraNote.value = `${amount} is the same as ${label}, which customers already start on.`
    return
  }
  if (!ordering.value.alsoOffer.includes(intervalKey(preset))) toggleExtra(intervalKey(preset))
  extraNote.value = `${amount} is the same as ${label}, so ${label} is ticked.`
}

const setExtraUnit = (index, unit) => {
  ordering.value.customExtras[index].unit = unit
  settleExtra(index)
}

const addExtra = () => {
  ordering.value.customExtras = [...ordering.value.customExtras, { count: null, unit: 'days' }]
}

const addWeekdayExtra = () => {
  ordering.value.customExtras = [...ordering.value.customExtras, { unit: WEEKDAY_SCHEDULE, weekdays: [] }]
}

const subscriptionStoreIds = computed(() => ordering.value.subscriptionStoreIds ?? props.stores.map(s => s.id))

const startingStoreOptions = computed(() => [
  { name: 'No store picked', value: null },
  ...props.stores.filter(s => subscriptionStoreIds.value.includes(s.id)).map(s => ({ name: s.name, value: s.id }))
])

const toggleSubscriptionStore = id => {
  const ids = subscriptionStoreIds.value
  const next = ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]
  ordering.value.subscriptionStoreIds = next
  if (!next.includes(ordering.value.defaultSubscriptionStoreId)) ordering.value.defaultSubscriptionStoreId = null
}

const setStartingStore = id => {
  ordering.value.subscriptionStoreIds = subscriptionStoreIds.value
  ordering.value.defaultSubscriptionStoreId = id
}

onMounted(() => {
  if (wizard.editing) return
  settleRepeat()
  for (let index = ordering.value.customExtras.length - 1; index >= 0; index -= 1) settleExtra(index)
})

return (_ctx, _cache) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(WizardSection, _normalizeProps(_guardReactiveProps(_unref(sectionCopy)('every', _unref(wizard).data))), {
      default: _withCtx(() => [
        _createVNode(PillRadioGroup, {
          "model-value": ordering.value.every,
          options: frequencyOptions,
          label: _unref(sectionCopy)('every', _unref(wizard).data).title,
          "onUpdate:modelValue": setEvery
        }, null, 8, ["model-value", "label"]),
        (weekdaysPicked.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_1, _toDisplayString(WEEKDAYS_HELP)))
          : _createCommentVNode("", true),
        (weekdaysPicked.value)
          ? (_openBlock(), _createBlock(WeekdayPicker, {
              key: 1,
              modelValue: ordering.value.weekdays,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => ((ordering.value.weekdays) = $event)),
              days: _unref(WEEKDAYS),
              label: "Repeat on"
            }, null, 8, ["modelValue", "days"]))
          : _createCommentVNode("", true),
        (customPicked.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_2, _toDisplayString(CUSTOM_HELP)))
          : _createCommentVNode("", true),
        (customPicked.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createVNode(AmountField, {
                modelValue: ordering.value.custom.count,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = $event => ((ordering.value.custom.count) = $event)),
                class: "w-24",
                kind: "integer",
                name: "Every",
                onChange: settleRepeat
              }, null, 8, ["modelValue"]),
              _createVNode(SegmentedControl, {
                "model-value": ordering.value.custom.unit,
                options: _unref(INTERVAL_UNITS),
                "onUpdate:modelValue": setUnit
              }, null, 8, ["model-value", "options"])
            ]))
          : _createCommentVNode("", true),
        (repeatNote.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_4, _toDisplayString(repeatNote.value), 1))
          : _createCommentVNode("", true),
        _cache[6] || (_cache[6] = _createElementVNode("div", { class: "h-px bg-[#eef0f3]" }, null, -1)),
        _createElementVNode("div", _hoisted_5, [
          _createVNode(ToggleRow, {
            modelValue: ordering.value.canSwitch,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = $event => ((ordering.value.canSwitch) = $event)),
            title: switching.value.label,
            description: switching.value.help
          }, null, 8, ["modelValue", "title", "description"])
        ]),
        (ordering.value.canSwitch)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _cache[5] || (_cache[5] = _createElementVNode("p", { class: "text-sm font-medium text-gray-700" }, "They can also choose", -1)),
              _createElementVNode("div", _hoisted_7, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(extraOptions.value, (preset) => {
                  return (_openBlock(), _createElementBlock("button", {
                    key: _unref(intervalKey)(preset),
                    type: "button",
                    "aria-pressed": ordering.value.alsoOffer.includes(_unref(intervalKey)(preset)),
                    class: _normalizeClass(["inline-flex items-center gap-1.5 rounded-full border px-4 py-[9px] text-sm transition", 
            ordering.value.alsoOffer.includes(_unref(intervalKey)(preset))
              ? 'border-gray-900 bg-gray-900 font-semibold text-white'
              : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400'
          ]),
                    onClick: $event => (toggleExtra(_unref(intervalKey)(preset)))
                  }, [
                    (ordering.value.alsoOffer.includes(_unref(intervalKey)(preset)))
                      ? (_openBlock(), _createBlock(_unref(CheckIcon), {
                          key: 0,
                          class: "h-4 w-4",
                          "aria-hidden": "true"
                        }))
                      : _createCommentVNode("", true),
                    _createElementVNode("span", null, _toDisplayString(_unref(intervalLabel)(preset)), 1)
                  ], 10, _hoisted_8))
                }), 128))
              ]),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(ordering.value.customExtras, (extra, index) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: index,
                  class: "flex flex-wrap items-end gap-3"
                }, [
                  (extra.unit === _unref(WEEKDAY_SCHEDULE))
                    ? (_openBlock(), _createBlock(WeekdayPicker, {
                        key: 0,
                        modelValue: extra.weekdays,
                        "onUpdate:modelValue": $event => ((extra.weekdays) = $event),
                        days: _unref(WEEKDAYS),
                        label: "Also repeat on"
                      }, null, 8, ["modelValue", "onUpdate:modelValue", "days"]))
                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        _createVNode(AmountField, {
                          modelValue: extra.count,
                          "onUpdate:modelValue": $event => ((extra.count) = $event),
                          class: "w-24",
                          kind: "integer",
                          name: "Every",
                          onChange: $event => (settleExtra(index))
                        }, null, 8, ["modelValue", "onUpdate:modelValue", "onChange"]),
                        _createVNode(SegmentedControl, {
                          "model-value": extra.unit,
                          options: _unref(INTERVAL_UNITS),
                          "onUpdate:modelValue": $event => (setExtraUnit(index, $event))
                        }, null, 8, ["model-value", "options", "onUpdate:modelValue"])
                      ], 64)),
                  _createVNode(WizardButton, {
                    onClick: $event => (removeExtra(index))
                  }, {
                    default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                      _createTextVNode("Remove", -1)
                    ]))]),
                    _: 1
                  }, 8, ["onClick"])
                ]))
              }), 128)),
              _createElementVNode("div", { class: "flex flex-wrap gap-4" }, [
                _createElementVNode("button", {
                  type: "button",
                  class: "text-sm font-medium text-gray-600 underline hover:text-gray-900",
                  onClick: addExtra
                }, " Add a custom schedule "),
                _createElementVNode("button", {
                  type: "button",
                  class: "text-sm font-medium text-gray-600 underline hover:text-gray-900",
                  onClick: addWeekdayExtra
                }, " Add set weekdays ")
              ]),
              (extraNote.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_9, _toDisplayString(extraNote.value), 1))
                : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 16),
    _createVNode(WizardSection, _mergeProps(_unref(sectionCopy)('payment', _unref(wizard).data), { plain: "" }), {
      default: _withCtx(() => [
        _createVNode(SelectionGroup, {
          label: _unref(sectionCopy)('payment', _unref(wizard).data).title
        }, {
          default: _withCtx(({ groupName }) => [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(payments.value, (option) => {
              return (_openBlock(), _createBlock(SelectionCard, {
                key: option.value,
                name: groupName,
                icon: PAYMENT_ICONS[option.value],
                title: option.title,
                subtitle: option.subtitle,
                selected: ordering.value.autocharge === option.value,
                onSelect: $event => (ordering.value.autocharge = option.value)
              }, null, 8, ["name", "icon", "title", "subtitle", "selected", "onSelect"]))
            }), 128))
          ]),
          _: 1
        }, 8, ["label"])
      ]),
      _: 1
    }, 16),
    _createVNode(WizardSection, _normalizeProps(_guardReactiveProps(_unref(sectionCopy)('subscriptionStores', _unref(wizard).data))), {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_10, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.stores, (store) => {
            return (_openBlock(), _createBlock(ToggleRow, {
              key: store.id,
              title: store.name,
              "model-value": subscriptionStoreIds.value.includes(store.id),
              "onUpdate:modelValue": $event => (toggleSubscriptionStore(store.id))
            }, null, 8, ["title", "model-value", "onUpdate:modelValue"]))
          }), 128))
        ]),
        _cache[7] || (_cache[7] = _createElementVNode("div", { class: "h-px bg-[#eef0f3]" }, null, -1)),
        _createVNode(SelectInput, {
          "model-value": ordering.value.defaultSubscriptionStoreId,
          name: "Store picked when customers start",
          options: startingStoreOptions.value,
          "onUpdate:modelValue": setStartingStore
        }, null, 8, ["model-value", "options"])
      ]),
      _: 1
    }, 16),
    _createElementVNode("details", _hoisted_11, [
      _createElementVNode("summary", _hoisted_12, [
        _cache[8] || (_cache[8] = _createTextVNode(" More options ", -1)),
        _createElementVNode("span", _hoisted_13, "· Minimum orders: " + _toDisplayString(ordering.value.minimumOrders ?? 'not set'), 1)
      ]),
      _createElementVNode("div", _hoisted_14, [
        _createVNode(NumberStepper, {
          modelValue: ordering.value.minimumOrders,
          "onUpdate:modelValue": _cache[3] || (_cache[3] = $event => ((ordering.value.minimumOrders) = $event)),
          name: "Paid orders before customers can skip",
          min: 1,
          description: _unref(sectionCopy)('minimumOrders', _unref(wizard).data).description
        }, null, 8, ["modelValue", "description"])
      ])
    ])
  ], 64))
}
}

}